import { useState, useEffect } from 'react';

/**
 * Detect if device is touch-enabled (mobile/tablet).
 * Uses touch capability as primary indicator.
 */
function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Hook to detect mobile/touch devices.
 * Returns true for touch-enabled devices (phones, tablets).
 *
 * This is useful for:
 * - Showing swipe gestures vs click interactions
 * - Bottom sheets vs dropdown menus
 * - Touch-optimized vs mouse-optimized UIs
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => isTouchDevice());

  useEffect(() => {
    // Re-check on mount (in case SSR detected differently)
    setIsMobile(isTouchDevice());
  }, []);

  return isMobile;
}

/**
 * Hook to detect screen width breakpoints.
 * Useful for responsive layouts that differ from touch detection.
 */
export function useBreakpoint(breakpoint: number = 768): boolean {
  const [isBelow, setIsBelow] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    const checkWidth = () => {
      setIsBelow(window.innerWidth < breakpoint);
    };

    // Check on mount
    checkWidth();

    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, [breakpoint]);

  return isBelow;
}
